import { makeAutoObservable, observable, reaction, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { CapacityHoursMode } from "@/components/capacity-planning/scan/scan-math";
import type { RootStore } from "./root.store";
import type {
  TWorkspaceCapacityAllocation,
  TWorkspaceCapacityGroupBy,
  TWorkspaceCapacityMember,
  TWorkspaceCapacityMemberDay,
  TWorkspaceCapacitySummary,
} from "@/types/workspace-capacity";
import { CAPACITY_MEMBER_PAGE_SIZE, CAPACITY_TASK_PAGE_SIZE } from "@/lib/capacity-pagination";
import {
  addDays,
  addMonths,
  buildWindow,
  type CapacityWindow,
  parseIsoDate,
  periodStart,
  windowStart,
} from "@/components/capacity-planning/capacity-axis";
import {
  MAX_COLUMNS,
  MIN_COLUMNS,
  type CapacityScanZoom,
} from "@/components/capacity-planning/capacity-planning-constants";

export type CapacityPlanningRowGroup = "member" | "team" | "project";

/**
 * `"utilization_desc"` is the scan-first default. It ranks the highest load
 * first and keeps people without configured hours at the end.
 *
 * There is deliberately no ascending-utilization twin: "least utilized" and
 * `"room_desc"` ("Most room first") answered the same question — who can take
 * work — and read as duplicates in the menu (Eric, 2026-09-14). `"room_desc"`
 * is the survivor because free *hours* is what a planner acts on; a `?sort=`
 * deep link naming the removed value falls through to the default.
 */
export type CapacityPeopleSort = "utilization_desc" | "name_asc" | "name_desc" | "room_desc";

export type CapacityUtilizationBucket = "over" | "near" | "target" | "under";

/**
 * Window (ms) a scheduled refresh waits, so a burst of write signals — a bulk
 * edit, or a save that both announces itself and calls the view's own refresh —
 * collapses into a single re-read. Short enough to feel immediate.
 */
const REFRESH_DEBOUNCE_MS = 250;

/**
 * Window (ms) the people search waits before it becomes a capacity read. The
 * search filters server-side (it has to narrow the member set before
 * pagination), so an un-debounced box would fire one request per keystroke.
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * How recently a refresh must have run for a window-focus refresh to be skipped.
 * Capacity reads are rollup-backed, so re-running them on every alt-tab is
 * wasteful; 10s still catches "I edited it in the other tab and came back".
 */
export const FOCUS_REFRESH_MIN_GAP_MS = 10_000;

/**
 * Team grouping is the default, and a collapsed team says nothing about who has
 * room. So on a fresh load the first teams expand on their own until roughly
 * this many people are on screen; the user's own toggles then take over.
 */
const AUTO_EXPAND_MEMBER_BUDGET = 30;

/** Toolbar capacity buckets → the server's status_filter values. */
const SERVER_STATUS_BY_BUCKET: Record<CapacityUtilizationBucket, string> = {
  over: "over",
  near: "near_cap",
  target: "on_target",
  under: "under",
};

/**
 * State for the Capacity Planning → People view: zoom/anchor window, team
 * grouping, row expansion, and cached read-only capacity data (summary +
 * lazily-loaded per-member allocation details).
 *
 * Scan-first defaults (decided 2026-09-03): `"week"` zoom, `"team"` row
 * grouping, and `"utilization_desc"` people sort. Month zoom is
 * hidden from this view — {@link setZoom} ignores `"month"`.
 */
/** Re-reads after an unassign, while the allocation rebuild it enqueued lands (NG-2659). */
const UNASSIGN_REREAD_DELAYS_MS = [1_500, 3_000, 6_000, 12_000];
/** After this the hidden bar is released whatever the reads said. */
const UNASSIGN_SETTLE_DEADLINE_MS = 30_000;

export class CapacityPlanningStore {
  zoom: TWorkspaceCapacityGroupBy = "week";
  /** Leftmost period start of the visible window. */
  anchorDate: Date;
  /**
   * How many periods the window spans. Owned by the view, which measures the
   * timeline band and calls {@link setVisibleColumns} — a wide monitor shows
   * more weeks instead of empty space to the right of the grid. Starts at the
   * zoom's minimum so the first read goes out before anything is measured.
   */
  visibleColumns: number = MIN_COLUMNS.week;
  rowGroup: CapacityPlanningRowGroup = "team";
  /** What the search box shows — updated on every keystroke so typing stays responsive. */
  search = "";
  /** What the capacity reads filter on — trails `search` by SEARCH_DEBOUNCE_MS. */
  debouncedSearch = "";
  peopleSort: CapacityPeopleSort = "utilization_desc";
  /** Draw the bars from logged hours instead of estimates. Off by default: the page plans, it does not audit. */
  showActuals = false;
  filterTeamIds = observable.set<string>();
  filterSkillIds = observable.set<string>();
  filterProgramIds = observable.set<string>();
  /** Project STATUSES to keep (NG-1458). Unlike the sets above this narrows the
   *  ALLOCATIONS, not just which people are listed. */
  filterProjectStateIds = observable.set<string>();
  filterJobRoleIds = observable.set<string>();
  /** Whether allocations from archived projects count (NG-1458). Off by default,
   *  which is a change from the behaviour this replaced — the server keeps the
   *  old answer when the param is absent, so the store states it on every read. */
  includeArchivedProjects = false;
  filterCapacityBucket: CapacityUtilizationBucket | null = null;
  expandedMembers = observable.set<string>();
  /** Groups (teams / projects) the user explicitly expanded. Everything else
   * renders collapsed, so summary refetches and new group pages never disturb
   * the user's expansion state. */
  expandedGroups = observable.set<string>();
  /** Set while the People view is mounted: a bare store read expands nothing
   *  (see `autoExpandGroups`). */
  private autoExpandArmed = false;
  /** The `grouping|filter` the first groups were last opened for, so the
   *  auto-expand runs once per arrival. */
  private autoExpandedFor: string | null = null;
  collapsedMemberProjects = observable.set<string>();

  summaryByKey = observable.map<string, TWorkspaceCapacitySummary>();
  /**
   * The last summary and team-member rows that loaded for the current *shape*
   * (slug + zoom + grouping + filters), keyed without the window.
   *
   * They exist so panning the dates does not blank the grid. Every capacity
   * cache is keyed by the window, so moving it made `current` undefined and the
   * whole table dropped to a skeleton for the length of the request — the
   * "stutter" the arrows were unusable through. The view falls back to these
   * while the new window's read is in flight, so the same people, teams and
   * expansion stay on screen and swap in place when the data lands.
   *
   * Consistency matters more than freshness here: the header's columns are
   * derived from the same rows, so a carried-over grid reads as the window it
   * actually holds rather than showing last week's bars under this week's
   * dates. {@link isWindowPending} is what says the two are out of step, and
   * the toolbar shows a spinner while they are.
   *
   * Bounded to one shape — {@link rememberShape} drops the previous shape's
   * entries — so this never grows with the filter permutations a session visits.
   */
  private carriedShape: string | null = null;
  private carriedSummary: TWorkspaceCapacitySummary | undefined = undefined;
  private carriedTeamMembers = observable.map<string, TWorkspaceCapacityMember[]>();
  private carriedDetails = observable.map<string, TWorkspaceCapacityMemberDay[]>();
  /** The window the three carried reads describe, so the view can position their
   *  bars against the range they actually belong to. */
  private carriedWindow: { startIso: string; endIso: string } | null = null;
  /** The window whose read failed, so the pending affordance stops claiming one
   *  is still on the way, and a window with nothing to carry over shows an error
   *  (`isSummaryUnavailable`). Cleared when that window is asked for again. */
  private failedSummaryKey: string | null = null;
  detailsByKey = observable.map<string, TWorkspaceCapacityMemberDay[]>();
  loadingMembers = observable.set<string>();
  /** Per-member server task pagination: next page offset + whether more remain. */
  taskPageByKey = observable.map<string, { offset: number; hasNext: boolean }>();

  /** Team-tab: per-team member heatmap rows, loaded lazily when a team expands. */
  teamMembersByKey = observable.map<string, TWorkspaceCapacityMember[]>();
  loadingTeams = observable.set<string>();
  /** Per-group server member pagination: next cursor + whether more rows remain. */
  teamMemberPageByKey = observable.map<string, { cursor: string | null; hasNext: boolean }>();

  isLoading = false;
  isLoadingMore = false;

  /** Cache keys read since the last `markReadsStale()`. Capacity data changes
   * from anywhere in the app (a work item rescheduled on its own page, a new
   * assignment), so a cached read must not outlive a visit to the view: keys
   * absent here are refetched once, in place, on the next pass — the cached
   * data keeps rendering until the fresh read lands (#3712). Deliberately a
   * plain Set: freshness drives fetching, not rendering. */
  private freshKeys = new Set<string>();

  /** Pending coalesced refresh (see `scheduleRefresh`), or null when idle. */
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  /** When the last refresh was kicked off — the dedupe/throttle reference. */
  private lastRefreshAt = 0;
  /** Allocations unassigned from a member whose removal the grid has not read
   *  back yet, keyed `<workItemId>:<memberId>` (NG-2659). The grid reads
   *  allocation rows a background rebuild writes, so a re-read straight after
   *  the unassign still carries the bar; {@link detailsFor} hides it until a
   *  read comes back without it. */
  hiddenAllocations = observable.set<string>();
  /** Per hidden allocation: the re-read timers and the reaction that clears it. */
  private unassignSettlers = new Map<string, () => void>();
  /** Pending debounce for the people search box, or null when idle. */
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  /** Embedded instances (a team page) pin every capacity read to one team;
   *  the toolbar hides the Team filter and the pin is not a removable chip. */
  readonly pinnedTeamId: string | null;

  /** The project equivalent, for a project's Resources tab: every read is
   *  filtered to the people with work on that project. Same contract as
   *  {@link pinnedTeamId} — a pin, not a chip the user can clear. */
  readonly pinnedProjectId: string | null;
  /** Projects an EMBEDDING surface scopes this store to — the dashboard detail
   *  page's member panel, which must open over the same project set its widgets
   *  were computed against (#3706). A scope, not a filter: it is not a menu
   *  entry, has no chip, is not counted in {@link activeFilterCount} and is not
   *  in the URL. NG-1458 removed the toolbar's per-project filter this used to
   *  ride on. Sorted on write so {@link filterKey} is stable. */
  scopedProjectIds: string[] = [];

  private rootStore: RootStore;

  constructor(rootStore: RootStore, options?: { pinnedTeamId?: string; pinnedProjectId?: string }) {
    this.rootStore = rootStore;
    this.pinnedTeamId = options?.pinnedTeamId ?? null;
    this.pinnedProjectId = options?.pinnedProjectId ?? null;
    // A project-pinned grid lists people, not teams. Team grouping only ever
    // shows members through a team, so on a project's Resources tab anyone doing
    // the work without a team space silently disappears and the lead is left
    // reading empty "0 of 0" groups — which is the exact question that tab
    // exists to answer. The project scope has already narrowed the set.
    if (this.pinnedProjectId) this.rowGroup = "member";
    this.anchorDate = periodStart("day", new Date());
    makeAutoObservable(
      this,
      {
        rootStore: false,
        freshKeys: false,
        refreshTimer: false,
        lastRefreshAt: false,
        searchDebounceTimer: false,
        autoExpandArmed: false,
        autoExpandedFor: false,
        unassignSettlers: false,
      } as never,
      { autoBind: true },
    );
  }

  get window(): CapacityWindow {
    return buildWindow(this.zoom, this.anchorDate, this.visibleColumns);
  }

  /** The zoom's column bounds — `zoom` is never `"month"` in this view. */
  private get columnBounds(): { min: number; max: number } {
    const zoom = (this.zoom === "month" ? "week" : this.zoom) as CapacityScanZoom;
    return { min: MIN_COLUMNS[zoom], max: MAX_COLUMNS[zoom] };
  }

  /**
   * Resize the window to `columns` periods, clamped to the zoom's bounds. A
   * no-op when the count is unchanged, because the count is part of every
   * capacity cache key: without the guard a ResizeObserver firing mid-drag
   * would re-issue the whole read on every frame.
   */
  setVisibleColumns(columns: number): void {
    if (!Number.isFinite(columns)) return;
    const { min, max } = this.columnBounds;
    const next = Math.min(max, Math.max(min, Math.floor(columns)));
    if (next === this.visibleColumns) return;
    this.visibleColumns = next;
  }

  private get slug(): string {
    return this.rootStore.router.workspaceSlug ?? "";
  }

  /** Toolbar filters as request options for the capacity reads — Team /
   * Skill / Capacity / search apply server-side so pagination, rollups, and
   * groups reflect the filtered member set rather than just the loaded rows.
   * Search in particular *must* be server-side: filtering the loaded page
   * client-side hid anyone past the first page of members (#4168). */
  private get serverFilterOptions(): {
    teamIds?: string[];
    requiredSkillIds?: string[];
    programIds?: string[];
    projectStateIds?: string[];
    jobRoleIds?: string[];
    includeArchivedProjects?: boolean;
    statusFilter?: string;
    search?: string;
  } {
    const teamIds = this.pinnedTeamId ? [this.pinnedTeamId] : [...this.filterTeamIds].sort();
    // A pinned project or an embed's scope is the surface's own framing and
    // always applies. The toolbar's per-project FILTER is gone (NG-1458 replaced
    // it with the status one), so no user-set project narrowing reaches the read:
    // a filter with no menu entry and no chip is worse than one never applied.
    const projectIds = this.pinnedProjectId ? [this.pinnedProjectId] : this.scopedProjectIds;
    return {
      ...(teamIds.length > 0 ? { teamIds } : {}),
      ...(this.filterSkillIds.size > 0
        ? { requiredSkillIds: [...this.filterSkillIds].sort() }
        : {}),
      ...(projectIds.length > 0 ? { projectIds } : {}),
      ...(this.filterProgramIds.size > 0 ? { programIds: [...this.filterProgramIds].sort() } : {}),
      ...(this.filterProjectStateIds.size > 0
        ? { projectStateIds: [...this.filterProjectStateIds].sort() }
        : {}),
      ...(this.filterJobRoleIds.size > 0 ? { jobRoleIds: [...this.filterJobRoleIds].sort() } : {}),
      // Stated on every read, never elided on its default: absent means
      // "whatever the server did before NG-1458", which is the opposite of what
      // `false` asks for.
      includeArchivedProjects: this.includeArchivedProjects,
      ...(this.filterCapacityBucket
        ? { statusFilter: SERVER_STATUS_BY_BUCKET[this.filterCapacityBucket] }
        : {}),
      ...(this.debouncedSearch ? { search: this.debouncedSearch } : {}),
    };
  }

  /** Cache-key fragment for the active filters (filtered reads cache apart). */
  get filterKey(): string {
    const teams = [...this.filterTeamIds].sort().join(",");
    const skills = [...this.filterSkillIds].sort().join(",");
    const programs = [...this.filterProgramIds].sort().join(",");
    const states = [...this.filterProjectStateIds].sort().join(",");
    const roles = [...this.filterJobRoleIds].sort().join(",");
    const archived = this.includeArchivedProjects ? "1" : "0";
    const scope = this.pinnedProjectId ?? this.scopedProjectIds.join(",");
    return `${this.pinnedTeamId ?? ""}|${scope}|${teams}|${skills}|${programs}|${states}|${roles}|${archived}|${this.filterCapacityBucket ?? ""}|${this.debouncedSearch}`;
  }

  /** Treat every cached read as stale from here on. Called when a capacity view
   * is left, so returning to it refreshes rather than replaying the cache the
   * user already saw (#3712). Nothing is dropped — the next pass refetches each
   * visible read in place, so no row blanks into a loading state. */
  markReadsStale(): void {
    this.freshKeys.clear();
    // Leaving the view also drops any queued refresh: the next visit re-reads
    // anyway, so firing it at a view nobody is looking at is pure waste.
    this.cancelScheduledRefresh();
    // Same for an unassign still settling: the next visit's read is the answer.
    for (const key of [...this.unassignSettlers.keys()]) this.releaseHiddenAllocation(key);
  }

  /**
   * "Something changed somewhere — re-read what's on screen", for signals that
   * don't come from the view's own write paths: a work item edited by a modal
   * over the timeline, or by this user in another tab (#3712). Coalesced, so a
   * burst of writes (a bulk edit, a save that also announces itself through
   * `refreshAfterAssignment`) costs one refresh.
   *
   * `minGapMs` skips the refresh when one already happened that recently — the
   * focus path passes a gap so alt-tabbing doesn't re-run these rollup-backed
   * reads on every window switch.
   */
  scheduleRefresh(minGapMs = 0): void {
    if (this.refreshTimer) return;
    if (minGapMs > 0 && Date.now() - this.lastRefreshAt < minGapMs) return;
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      // A view-initiated refresh inside the debounce window (the peek sheet's
      // own save, a drag) has already re-read everything — don't do it twice.
      if (Date.now() - this.lastRefreshAt < REFRESH_DEBOUNCE_MS) return;
      this.refreshAfterAssignment();
    }, REFRESH_DEBOUNCE_MS);
  }

  private cancelScheduledRefresh(): void {
    if (!this.refreshTimer) return;
    clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  /** True when this key's cached data was read since the last staleness mark,
   * i.e. a guarded fetch can skip the network. */
  private isFresh(key: string): boolean {
    return this.freshKeys.has(key);
  }

  /** True when `map` holds this key's data *and* it was read this visit — the
   * one condition under which a guarded fetch may skip the network. */
  private hasFreshRead(map: { has(key: string): boolean }, key: string): boolean {
    return map.has(key) && this.isFresh(key);
  }

  private markFresh(key: string): void {
    this.freshKeys.add(key);
  }

  private summaryKey(): string {
    const win = this.window;
    return `${this.slug}|${win.startIso}|${win.endIso}|${this.zoom}|${this.rowGroup}|${this.filterKey}`;
  }

  /** The cache identity minus the window — what a date change does NOT alter. */
  private shapeKey(): string {
    return `${this.slug}|${this.zoom}|${this.rowGroup}|${this.filterKey}`;
  }

  /** Point the carry-over at `shape`, discarding another shape's leftovers. */
  private rememberShape(shape: string): void {
    if (this.carriedShape === shape) return;
    this.carriedShape = shape;
    this.carriedSummary = undefined;
    this.carriedWindow = null;
    this.carriedTeamMembers.clear();
    this.carriedDetails.clear();
  }

  /**
   * True while the grid is rendering a carried-over window rather than the one
   * the toolbar names — i.e. the current window's summary has not landed. False
   * on a first load, which has nothing to carry over and shows the skeleton.
   */
  get isWindowPending(): boolean {
    // Derived from `current`, not from `carriedSummary` directly, so it cannot
    // disagree with what the grid is actually rendering: a carry-over from
    // another shape is not shown, and must not read as pending either. A window
    // whose read FAILED is not pending either — the carried rows are then the
    // best available answer, and a spinner over them would promise an update
    // that is not coming.
    const key = this.summaryKey();
    if (this.failedSummaryKey === key) return false;
    if (!this.summaryByKey.has(key)) return this.current != null;

    // The summary landing is not the end of the swap. An expanded team's member
    // rows and an expanded person's task rows are separate reads on their own
    // window keys, and they land later — so a pending state that ended here
    // stopped the pulse while half the grid was still the previous window, and
    // then those rows changed under a UI claiming to be settled. Stay pending
    // until the rows on screen are the ones this window implies.
    //
    // Gated on something actually being in flight, so a read that failed leaves
    // the grid settled on what it has rather than pulsing for ever.
    if (this.loadingTeams.size === 0 && this.loadingMembers.size === 0) return false;
    if (this.rowGroup !== "member") {
      for (const group of this.current?.groups ?? []) {
        if (!this.expandedGroups.has(group.id)) continue;
        if (!this.teamMembersByKey.has(this.teamMembersKey(group.id))) return true;
      }
    }
    for (const memberId of this.expandedMembers) {
      if (!this.detailsByKey.has(this.detailKey(memberId))) return true;
    }
    return false;
  }

  /**
   * True when this window's summary read failed and nothing else can stand in
   * for it: a first load, or the first read after a grouping or filter change,
   * has no carry-over. Without this the grid drew its header over no rows and
   * the distribution strip counted zero people in every state, which reads as
   * a workspace with no work rather than a read that failed (NG-1934).
   *
   * A failed read over a carried-over window stays false. Those rows are a real
   * window, and {@link isWindowPending} already stops claiming a newer one is
   * on the way.
   */
  get isSummaryUnavailable(): boolean {
    return this.failedSummaryKey === this.summaryKey() && this.current == null;
  }

  /**
   * The window the data ON SCREEN belongs to, which is not the one the toolbar
   * names while a swap is in flight.
   *
   * The expanded rows position their bars as a percentage of this range, so
   * handing them the requested window while they still hold the previous one's
   * allocations would draw the right tasks in the wrong places. It follows the
   * carry-over exactly, and flips with it.
   *
   * So it asks whether `current` IS the carry-over, not whether the read is
   * still pending: a read that failed stops being pending while the carried
   * summary stays on screen, and keying on pending would then position last
   * window's rows against a range whose data never arrived.
   */
  get displayWindowIso(): { startIso: string; endIso: string } {
    const win = this.window;
    const showingCarryOver = this.current != null && !this.summaryByKey.has(this.summaryKey());
    if (showingCarryOver && this.carriedWindow) return this.carriedWindow;
    return { startIso: win.startIso, endIso: win.endIso };
  }

  private detailKey(memberId: string): string {
    const win = this.window;
    // The filters are part of the key because they are part of the request: a
    // details read cached under an unfiltered key would be served back under a
    // filtered view, and the week popover reconciles the two against each other.
    return `${this.slug}|${win.startIso}|${win.endIso}|${this.zoom}|${memberId}|${this.filterKey}`;
  }

  get current(): TWorkspaceCapacitySummary | undefined {
    const loaded = this.summaryByKey.get(this.summaryKey());
    if (loaded) return loaded;
    // Same shape, different window: keep the last one on screen rather than
    // blanking (see `carriedSummary`).
    return this.carriedShape === this.shapeKey() ? this.carriedSummary : undefined;
  }

  detailsFor(memberId: string): TWorkspaceCapacityMemberDay[] | undefined {
    const days = this.loadedDetailsFor(memberId);
    if (!days || this.hiddenAllocations.size === 0) return days;
    const hidden = (allocationId: string) =>
      this.hiddenAllocations.has(`${allocationId}:${memberId}`);
    if (!days.some((day) => (day.allocations ?? []).some((a) => hidden(a.id)))) return days;
    return days.map((day) => ({
      ...day,
      allocations: (day.allocations ?? []).filter((a) => !hidden(a.id)),
    }));
  }

  /** {@link detailsFor} as the server last answered, with nothing hidden. */
  private loadedDetailsFor(memberId: string): TWorkspaceCapacityMemberDay[] | undefined {
    const loaded = this.detailsByKey.get(this.detailKey(memberId));
    if (loaded) return loaded;
    // An expanded person's task rows carry over with everything else, or the
    // block collapses to a single skeleton line on every pan and the page
    // height jumps twice per click.
    return this.carriedShape === this.shapeKey() ? this.carriedDetails.get(memberId) : undefined;
  }

  /**
   * Take a work item's bar off a member's row the moment it is unassigned
   * (NG-2659). Returns the undo for a failed unassign.
   */
  hideUnassignedAllocation(workItemId: string, memberId: string): () => void {
    const key = `${workItemId}:${memberId}`;
    this.hiddenAllocations.add(key);
    return () => this.releaseHiddenAllocation(key);
  }

  /**
   * Once the unassign has saved, keep re-reading until the grid's allocation
   * rows drop the bar. They come from a background rebuild the write only
   * enqueues, so one re-read straight after it is usually too early. The bar
   * stays hidden until a read without it lands, or the deadline passes, after
   * which the grid shows whatever the server says.
   */
  settleUnassignedAllocation(
    workItemId: string,
    memberId: string,
    reread: () => void = () => this.refreshAfterAssignment(),
  ): void {
    const key = `${workItemId}:${memberId}`;
    this.unassignSettlers.get(key)?.();
    const timers = UNASSIGN_REREAD_DELAYS_MS.map((delay) => setTimeout(reread, delay));
    const deadline = setTimeout(
      () => this.releaseHiddenAllocation(key),
      UNASSIGN_SETTLE_DEADLINE_MS,
    );
    const stopWatching = reaction(
      () => {
        const days = this.loadedDetailsFor(memberId);
        return days === undefined
          ? true
          : days.some((day) => (day.allocations ?? []).some((a) => a.id === workItemId));
      },
      (stillThere) => {
        if (!stillThere) this.releaseHiddenAllocation(key);
      },
    );
    this.unassignSettlers.set(key, () => {
      for (const timer of timers) clearTimeout(timer);
      clearTimeout(deadline);
      stopWatching();
    });
  }

  private releaseHiddenAllocation(key: string): void {
    this.unassignSettlers.get(key)?.();
    this.unassignSettlers.delete(key);
    this.hiddenAllocations.delete(key);
  }

  setZoom(zoom: TWorkspaceCapacityGroupBy): void {
    // Month zoom is hidden from the scan-first view — a stray call (e.g. a
    // stale control) is a no-op rather than surfacing an unreachable state.
    if (zoom === "month") return;
    if (zoom === this.zoom) return;
    this.zoom = zoom;
    this.anchorDate = periodStart(zoom, new Date());
    // The two zooms have different column bounds, so carrying the old count
    // over unclamped would ask for a window the new zoom does not allow. The
    // view re-measures and corrects on its next pass.
    const { min, max } = this.columnBounds;
    this.visibleColumns = Math.min(max, Math.max(min, this.visibleColumns));
  }

  goToday(): void {
    this.anchorDate = periodStart(this.zoom, new Date());
  }

  goPrev(): void {
    this.shift(-1);
  }

  goNext(): void {
    this.shift(1);
  }

  /**
   * Page the window by its whole visible range: the next window starts the day
   * AFTER the last day on screen, at the same column count, and the previous one
   * ends the day before the first (Aaron, 2026-09-15).
   *
   * Stepping one period instead — which is what this did between 2026-09-14 and
   * now — made the arrows useless at the widths the responsive window reaches: a
   * one-day nudge across twenty day columns barely moves anything, and it takes
   * twenty clicks to reach a range you have not already read. Paging also keeps
   * the two zooms consistent, which is the property that was actually being
   * asked for.
   *
   * Anchored off `viewStart` rather than `anchorDate` so the arithmetic starts
   * from the window the user can see. For week zoom the span is a whole number
   * of weeks, so the Monday anchor survives and columns stay calendar weeks.
   */
  private shift(direction: number): void {
    if (this.zoom === "month") {
      this.anchorDate = addMonths(this.anchorDate, direction * this.visibleColumns);
      return;
    }
    const win = this.window;
    this.anchorDate = addDays(win.viewStart, direction * win.visibleDays);
  }

  toggleActuals(): void {
    this.showActuals = !this.showActuals;
  }

  get hoursMode(): CapacityHoursMode {
    return this.showActuals ? "actual" : "estimated";
  }

  /** Scope every read to these projects (an embed's framing — see
   *  {@link scopedProjectIds}). Empty clears the scope. */
  setScopedProjectIds(projectIds: readonly string[]): void {
    this.scopedProjectIds = [...new Set(projectIds)].sort();
  }

  setRowGroup(rowGroup: CapacityPlanningRowGroup): void {
    if (rowGroup === this.rowGroup) return;
    this.rowGroup = rowGroup;
    // Expansion is keyed by group id, and ids belong to the axis that produced
    // them: teams and projects share no ids. Carrying the old set over leaves
    // the view collapsed against a set it can never match, and the auto-expand
    // that fills the first rows bails because the set is not empty — so every
    // group reads zero until the user opens one by hand.
    this.expandedGroups.clear();
  }

  setSearch(value: string): void {
    this.search = value;
    if (this.searchDebounceTimer != null) clearTimeout(this.searchDebounceTimer);
    const trimmed = value.trim();
    // Clearing the box restores the unfiltered list immediately — waiting out
    // the debounce to show *more* rows just reads as lag.
    if (trimmed === "") {
      this.searchDebounceTimer = null;
      this.debouncedSearch = "";
      return;
    }
    this.searchDebounceTimer = setTimeout(() => {
      runInAction(() => {
        this.searchDebounceTimer = null;
        this.debouncedSearch = trimmed;
      });
    }, SEARCH_DEBOUNCE_MS);
  }

  setPeopleSort(sort: CapacityPeopleSort): void {
    this.peopleSort = sort;
  }

  /**
   * Replace a whole filter selection in one write (NG-1604).
   *
   * The shared filter control hands back the next selection as an array, where
   * the `toggle*` methods below take one id at a time. Diffing the two in the
   * component looked equivalent — exactly one id differs per click — but it is
   * N separate mutations, and `filterKey` (which `people-view.tsx` refetches
   * off) changes once per id. That is fine for a click and wrong for anything
   * that ever changes several at once: a select-all, a saved preset, a
   * clear-this-category row would each fire a request per intermediate state.
   *
   * `replace` is what the URL-hydration path has always used, so this is the
   * public form of a write the store already did.
   */
  setTeamFilter(teamIds: string[]): void {
    this.filterTeamIds.replace(teamIds);
  }

  setSkillFilter(skillIds: string[]): void {
    this.filterSkillIds.replace(skillIds);
  }

  setProgramFilter(programIds: string[]): void {
    this.filterProgramIds.replace(programIds);
  }

  setProjectStateFilter(stateIds: string[]): void {
    this.filterProjectStateIds.replace(stateIds);
  }

  setJobRoleFilter(jobRoleIds: string[]): void {
    this.filterJobRoleIds.replace(jobRoleIds);
  }

  toggleTeamFilter(teamId: string): void {
    if (this.filterTeamIds.has(teamId)) this.filterTeamIds.delete(teamId);
    else this.filterTeamIds.add(teamId);
  }

  toggleSkillFilter(skillId: string): void {
    if (this.filterSkillIds.has(skillId)) this.filterSkillIds.delete(skillId);
    else this.filterSkillIds.add(skillId);
  }

  toggleProgramFilter(programId: string): void {
    if (this.filterProgramIds.has(programId)) this.filterProgramIds.delete(programId);
    else this.filterProgramIds.add(programId);
  }

  toggleProjectStateFilter(stateId: string): void {
    if (this.filterProjectStateIds.has(stateId)) this.filterProjectStateIds.delete(stateId);
    else this.filterProjectStateIds.add(stateId);
  }

  toggleJobRoleFilter(jobRoleId: string): void {
    if (this.filterJobRoleIds.has(jobRoleId)) this.filterJobRoleIds.delete(jobRoleId);
    else this.filterJobRoleIds.add(jobRoleId);
  }

  setIncludeArchivedProjects(include: boolean): void {
    this.includeArchivedProjects = include;
  }

  setCapacityBucketFilter(bucket: CapacityUtilizationBucket | null): void {
    this.filterCapacityBucket = bucket;
  }

  get activeFilterCount(): number {
    return (
      this.filterTeamIds.size +
      this.filterSkillIds.size +
      this.filterProgramIds.size +
      this.filterProjectStateIds.size +
      this.filterJobRoleIds.size +
      // "Include archived" counts only when it is ON: off is the default, and a
      // Filter button reading "1" on a page nobody filtered is noise.
      (this.includeArchivedProjects ? 1 : 0) +
      (this.filterCapacityBucket ? 1 : 0)
    );
  }

  clearFilters(): void {
    this.filterTeamIds.clear();
    this.filterSkillIds.clear();
    this.filterProgramIds.clear();
    this.filterProjectStateIds.clear();
    this.filterJobRoleIds.clear();
    this.includeArchivedProjects = false;
    this.filterCapacityBucket = null;
  }

  /** Hydrate display state from URL query params (deep links / bookmarks).
   * Only the keys present apply; values are pre-validated by the caller. */
  applyUrlState(state: {
    zoom?: TWorkspaceCapacityGroupBy;
    anchorIso?: string;
    rowGroup?: CapacityPlanningRowGroup;
    sort?: CapacityPeopleSort;
    search?: string;
    teamIds?: string[];
    skillIds?: string[];
    programIds?: string[];
    projectStateIds?: string[];
    jobRoleIds?: string[];
    includeArchivedProjects?: boolean;
    capacityBucket?: CapacityUtilizationBucket;
    showActuals?: boolean;
  }): void {
    if (state.zoom) {
      this.zoom = state.zoom;
      // Assigned directly rather than through `setZoom` (which also re-anchors
      // on today, and a deep link carries its own anchor), so the column clamp
      // it does has to happen here too — a `?zoom=day` link otherwise opened a
      // six-DAY window, week zoom's minimum applied to the wrong unit.
      const { min, max } = this.columnBounds;
      this.visibleColumns = Math.min(max, Math.max(min, this.visibleColumns));
    }
    if (state.showActuals != null) this.showActuals = state.showActuals;
    if (state.anchorIso) {
      const parsed = parseIsoDate(state.anchorIso);
      // `windowStart`, not `periodStart`: a day-zoom anchor is taken verbatim,
      // so a window the arrows stepped off the week boundary survives being
      // bookmarked and reopened.
      if (!Number.isNaN(parsed.getTime())) this.anchorDate = windowStart(this.zoom, parsed);
    }
    if (state.rowGroup) this.rowGroup = state.rowGroup;
    if (state.sort) this.peopleSort = state.sort;
    if (state.search != null) {
      // A deep link is not typing: the box shows the term and the FIRST read
      // filters on it. Leaving `debouncedSearch` empty here fetched the whole
      // member list, which reads as "person missing" once they fall past the
      // first page (the stg nightly, 123 members at 50 a page).
      if (this.searchDebounceTimer != null) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
      this.search = state.search;
      this.debouncedSearch = state.search.trim();
    }
    if (state.teamIds) this.filterTeamIds.replace(state.teamIds);
    if (state.skillIds) this.filterSkillIds.replace(state.skillIds);
    if (state.programIds) this.filterProgramIds.replace(state.programIds);
    if (state.projectStateIds) this.filterProjectStateIds.replace(state.projectStateIds);
    if (state.jobRoleIds) this.filterJobRoleIds.replace(state.jobRoleIds);
    if (state.includeArchivedProjects != null)
      this.includeArchivedProjects = state.includeArchivedProjects;
    if (state.capacityBucket) this.filterCapacityBucket = state.capacityBucket;
  }

  toggleGroup(groupId: string): void {
    if (this.expandedGroups.has(groupId)) {
      this.expandedGroups.delete(groupId);
      return;
    }
    // Expanding — load this team's members lazily (nothing hidden is fetched
    // on the initial collapsed load).
    this.expandedGroups.add(groupId);
    const group = this.current?.groups?.find((g) => g.id === groupId);
    if (group) void this.fetchTeamMembers(groupId, group.member_ids ?? []);
  }

  /**
   * Fresh team-grouped load: open the first groups so the page answers "who has
   * room" without a click. Once per grouping/filter, and never over a state the
   * user has already set.
   *
   * Deliberately NOT per window. Keyed on the date it re-ran on every pan, and a
   * user who had collapsed every team got them re-opened under the cursor each
   * time: the auto-expand is about arriving at a grouping, not about which
   * dates are on screen.
   *
   * `fetchSummary` calls this in the action that stores the summary, so the
   * first teams' member reads are already in flight when the grid first draws
   * it. Opened from a view effect instead, the people-by-state strip drew one
   * frame counting nobody, "0 have room" and so on, before the reads began
   * (NG-1934). The view still calls it for a summary served from cache.
   *
   * Only while the view has armed it: expansion is what the People view does
   * on arrival, so a read made without the view, or landing after it has
   * unmounted, leaves every group as it was.
   */
  autoExpandGroups(summary: TWorkspaceCapacitySummary | undefined = this.current): void {
    if (!this.autoExpandArmed || !summary || this.rowGroup === "member") return;
    const key = `${this.rowGroup}|${this.filterKey}`;
    if (this.autoExpandedFor === key) return;
    this.autoExpandedFor = key;
    if (this.expandedGroups.size > 0) return;
    let budget = AUTO_EXPAND_MEMBER_BUDGET;
    for (const group of summary.groups ?? []) {
      if (budget <= 0) break;
      this.toggleGroup(group.id);
      budget -= group.member_count;
    }
  }

  /** The People view mounted: open the first groups of each grouping it arrives at. */
  armAutoExpand(): void {
    this.autoExpandArmed = true;
    this.autoExpandedFor = null;
  }

  /** The People view unmounted. The next visit opens the first groups again. */
  disarmAutoExpand(): void {
    this.autoExpandArmed = false;
    this.autoExpandedFor = null;
  }

  toggleMemberProject(key: string): void {
    if (this.collapsedMemberProjects.has(key)) this.collapsedMemberProjects.delete(key);
    else this.collapsedMemberProjects.add(key);
  }

  toggleMember(memberId: string): void {
    if (this.expandedMembers.has(memberId)) {
      this.expandedMembers.delete(memberId);
      return;
    }
    this.expandedMembers.add(memberId);
    // Cache-guarded: a no-op when this member's rows are already loaded *and*
    // fresh, a refetch when they were loaded on an earlier visit to the view.
    void this.fetchMemberDetails(memberId);
  }

  /**
   * The user opened one person's allocations for a period (the week popover).
   *
   * The popover draws from rows already on screen, so the open itself is the
   * read and the event fires on it. The re-read is forced rather than
   * cache-guarded: opening a week is a deliberate question about what is in it,
   * and the cache can be re-marked fresh by a refresh the user never saw — so a
   * guarded read here replays allocations that predate work added since, with
   * nothing on screen admitting it. One request per open, on a click, is the
   * right price for the popover being true. The existing rows stay on screen
   * while it resolves, so nothing blanks.
   */
  viewMemberDetails(memberId: string, source: AnalyticsSource): void {
    track("member_capacity_viewed", { member_id: memberId, source });
    void this.fetchMemberDetails(memberId, true);
  }

  async fetchSummary(): Promise<void> {
    const slug = this.slug;
    if (!slug) return;
    const win = this.window;
    const key = this.summaryKey();
    runInAction(() => {
      this.isLoading = true;
      if (this.failedSummaryKey === key) this.failedSummaryKey = null;
    });
    try {
      const summary = await this.rootStore.workspaceCapacityService.getWorkspaceCapacity(
        slug,
        win.startIso,
        win.endIso,
        this.zoom,
        {
          includeTotals: false,
          // Team groups load their members lazily on expand (fetchTeamMembers);
          // the initial load is just the collapsed team list. Flat (member) mode
          // has no groups, so this is a no-op there.
          includeGroupMembers: false,
          // Team mode renders groups only (members load lazily) — skip the flat
          // member-row computation entirely. Flat mode needs the rows.
          ...(this.rowGroup === "member" ? {} : { includeMembers: false }),
          includeMemberMetrics: false,
          memberPageSize: CAPACITY_MEMBER_PAGE_SIZE,
          rowGroup: this.rowGroup,
          // Project grouping enumerates every project (same cap as the
          // project-detail picker); the default group page is too small.
          ...(this.rowGroup === "project" ? { groupPageSize: 100 } : {}),
          ...this.serverFilterOptions,
        },
      );
      runInAction(() => {
        this.summaryByKey.set(key, summary);
        // This window becomes the one to fall back to while the NEXT one loads —
        // but only if it is still the window being asked for. A response that
        // lost the race to a later one must not become the carry-over, or the
        // grid would fall back to a window the user has already moved past.
        if (this.summaryKey() === key) {
          this.rememberShape(this.shapeKey());
          this.carriedSummary = summary;
          this.carriedWindow = { startIso: win.startIso, endIso: win.endIso };
          // In the same action, so the grid never draws this summary with no
          // team read in flight (see `autoExpandGroups`).
          this.autoExpandGroups(summary);
        }
      });
      // Groups render collapsed unless explicitly expanded, so the refetch
      // itself never disturbs expansion. Re-sync expanded groups' member rows
      // for this window/filter key so an expanded chevron always has (or is
      // loading) its rows — fetchTeamMembers is cache-guarded, already-loaded
      // keys are a no-op. Skip stale responses (the user moved to another key).
      if (this.summaryKey() === key) {
        if (this.rowGroup !== "member") {
          for (const group of summary.groups ?? []) {
            if (!this.expandedGroups.has(group.id)) continue;
            void this.fetchTeamMembers(group.id, group.member_ids ?? []);
          }
        }
        // An expanded member's task rows come from a separate per-member read,
        // so re-sync those too: returning to the view marks reads stale, and
        // without this the expanded rows would keep rendering the previous
        // visit's dates/assignments until the user collapsed and re-expanded
        // them (#3712). Cache-guarded, so a same-visit refetch is a no-op.
        for (const memberId of this.expandedMembers) {
          void this.fetchMemberDetails(memberId);
        }
      }
    } catch (error) {
      // Swallowed and logged, like every other read in this store — the caller
      // is a `void`-ed effect, so throwing here only produced an unhandled
      // rejection. Marking the key failed stops the toolbar spinner. Where an
      // earlier window was loaded, the grid keeps it as the best answer there is;
      // where none was, `isSummaryUnavailable` turns the board into an error with
      // a retry (NG-1934).
      console.error("Failed to load the capacity summary", {
        slug,
        start: win.startIso,
        end: win.endIso,
        error,
      });
      runInAction(() => {
        this.failedSummaryKey = key;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Optimistically move a work item's bar to new dates across every cached
   * read, so a drag-drop reschedule lands instantly without waiting on the
   * server. Returns a revert closure that restores the prior dates verbatim —
   * call it if the backend write fails. (Heat-tile hours stay on the old
   * day distribution until the post-success refetch trues them up; only the
   * bar's span reads from these date fields.)
   */
  optimisticallyReschedule(workItemId: string, startIso: string, targetIso: string): () => void {
    const snapshots: {
      alloc: TWorkspaceCapacityAllocation;
      start: string | null;
      target: string | null;
    }[] = [];
    const visitDays = (days: TWorkspaceCapacityMemberDay[] | undefined) => {
      for (const day of days ?? []) {
        for (const alloc of day.allocations ?? []) {
          if (alloc.id !== workItemId) continue;
          snapshots.push({ alloc, start: alloc.start_date, target: alloc.target_date });
        }
      }
    };
    runInAction(() => {
      for (const days of this.detailsByKey.values()) visitDays(days);
      for (const summary of this.summaryByKey.values())
        for (const member of summary.members ?? []) visitDays(member.days);
      for (const members of this.teamMembersByKey.values())
        for (const member of members) visitDays(member.days);
      for (const snap of snapshots) {
        snap.alloc.start_date = startIso;
        snap.alloc.target_date = targetIso;
      }
    });
    return () => {
      runInAction(() => {
        for (const snap of snapshots) {
          snap.alloc.start_date = snap.start;
          snap.alloc.target_date = snap.target;
        }
      });
    };
  }

  /**
   * An allocation changed (dates, assignee): every cached read in this store
   * may now be stale. Refetch what's on screen IN PLACE — the visible window
   * keeps rendering its previous data until fresh data overwrites it (clearing
   * first blanked the whole timeline into the loading state on every drop) —
   * and drop only the caches that aren't currently visible.
   *
   * Called directly by the view's own write paths (drag, bar menu, peek, member
   * panel) and indirectly by `scheduleRefresh` for changes that came from
   * outside the view.
   */
  refreshAfterAssignment(): void {
    this.lastRefreshAt = Date.now();
    this.cancelScheduledRefresh();
    // Every read is stale by definition here; the visible ones are force-fetched
    // below (which re-marks them fresh) and the rest are evicted, so no marker
    // outlives the data it described.
    this.freshKeys.clear();
    const expandedTeams =
      this.rowGroup !== "member"
        ? (this.current?.groups ?? []).filter((g) => this.expandedGroups.has(g.id))
        : [];

    // Evict the summaries and team pages that are NOT on screen; the visible
    // ones refetch in place below so the UI never unmounts into a loading state.
    const keepSummary = this.summaryKey();
    for (const key of [...this.summaryByKey.keys()]) {
      if (key !== keepSummary) this.summaryByKey.delete(key);
    }
    // Member details are deliberately NOT evicted. `freshKeys.clear()` above has
    // already made every entry stale, so a guarded read refetches on its next
    // access and the popover's open forces one anyway — deleting bought nothing
    // but a blank. It also assumed "visible" meant "row expanded", but a member's
    // details are on screen through the week popover too, and that member is not
    // in `expandedMembers`. So a refresh landing while a popover was open — a
    // window focus is enough — dropped its details, the list fell back to the
    // skeleton, and the rows the user was reading unmounted under the cursor
    // (NG-1213: "element is not stable", then "detached"). Stale-but-present is
    // what the summary does; details now do the same.
    const keepTeams = new Set(expandedTeams.map((group) => this.teamMembersKey(group.id)));
    for (const key of [...this.teamMembersByKey.keys()]) {
      if (!keepTeams.has(key)) {
        this.teamMembersByKey.delete(key);
        this.teamMemberPageByKey.delete(key);
      }
    }

    void this.fetchSummary();
    for (const group of expandedTeams) {
      void this.fetchTeamMembers(group.id, group.member_ids ?? [], true);
    }
    for (const memberId of this.expandedMembers) {
      void this.fetchMemberDetails(memberId, true);
    }
  }

  private teamMembersKey(teamId: string): string {
    const win = this.window;
    return `${this.slug}|${win.startIso}|${win.endIso}|${this.zoom}|${teamId}|${this.filterKey}`;
  }

  teamMembersFor(teamId: string): TWorkspaceCapacityMember[] | undefined {
    const loaded = this.teamMembersByKey.get(this.teamMembersKey(teamId));
    if (loaded) return loaded;
    // The group rows carry over with the summary, or an expanded team would
    // collapse to its header on every date change while its members reload.
    return this.carriedShape === this.shapeKey() ? this.carriedTeamMembers.get(teamId) : undefined;
  }

  /** True while a group's members are loaded but more pages remain server-side. */
  groupHasMoreMembers(teamId: string): boolean {
    return this.teamMemberPageByKey.get(this.teamMembersKey(teamId))?.hasNext ?? false;
  }

  /** Load a team's members' full cross-project daily load (lean member-rollup
   * path), one server page at a time. A forced refresh re-walks pages up to the
   * previously-loaded row count so refresh-in-place doesn't shrink the list. */
  async fetchTeamMembers(teamId: string, memberIds: string[], force = false): Promise<void> {
    const slug = this.slug;
    if (!slug) return;
    const win = this.window;
    const key = this.teamMembersKey(teamId);
    if ((!force && this.hasFreshRead(this.teamMembersByKey, key)) || this.loadingTeams.has(teamId))
      return;
    if (memberIds.length === 0) {
      runInAction(() => {
        this.teamMembersByKey.set(key, []);
        this.teamMemberPageByKey.delete(key);
      });
      this.markFresh(key);
      return;
    }
    // A fresh load takes one page; a forced refresh re-walks pages until the
    // previously-loaded rows are covered (0 target → the loop runs once).
    const targetCount = force ? (this.teamMembersByKey.get(key)?.length ?? 0) : 0;
    runInAction(() => {
      this.loadingTeams.add(teamId);
    });
    try {
      const members: TWorkspaceCapacityMember[] = [];
      let cursor: string | null = null;
      do {
        const summary = await this.rootStore.workspaceCapacityService.getWorkspaceCapacity(
          slug,
          win.startIso,
          win.endIso,
          this.zoom,
          {
            includeTotals: false,
            rowGroup: "member",
            memberIds,
            memberPageSize: CAPACITY_MEMBER_PAGE_SIZE,
            memberCursor: cursor,
            ...this.serverFilterOptions,
          },
        );
        members.push(...(summary.members ?? []));
        const pagination = summary.members_pagination;
        cursor = pagination?.has_next ? (pagination.next_cursor ?? null) : null;
      } while (cursor && members.length < targetCount);
      const shape = this.shapeKey();
      runInAction(() => {
        this.teamMembersByKey.set(key, members);
        this.teamMemberPageByKey.set(key, { cursor, hasNext: cursor != null });
        if (this.carriedShape === shape) this.carriedTeamMembers.set(teamId, members);
      });
      this.markFresh(key);
    } catch (error) {
      console.error("Failed to load team capacity members", { teamId, memberIds, error });
    } finally {
      runInAction(() => {
        this.loadingTeams.delete(teamId);
      });
    }
  }

  /** Fetch and append the next member page of an expanded group (scroll sentinel). */
  async fetchMoreTeamMembers(teamId: string, memberIds: string[]): Promise<void> {
    const slug = this.slug;
    if (!slug) return;
    const win = this.window;
    const key = this.teamMembersKey(teamId);
    const page = this.teamMemberPageByKey.get(key);
    if (!page?.hasNext || !page.cursor || this.loadingTeams.has(teamId)) return;
    runInAction(() => {
      this.loadingTeams.add(teamId);
    });
    try {
      const summary = await this.rootStore.workspaceCapacityService.getWorkspaceCapacity(
        slug,
        win.startIso,
        win.endIso,
        this.zoom,
        {
          includeTotals: false,
          rowGroup: "member",
          memberIds,
          memberPageSize: CAPACITY_MEMBER_PAGE_SIZE,
          memberCursor: page.cursor,
          ...this.serverFilterOptions,
        },
      );
      const shape = this.shapeKey();
      runInAction(() => {
        const byId = new Map((this.teamMembersByKey.get(key) ?? []).map((m) => [m.id, m]));
        for (const m of summary.members ?? []) byId.set(m.id, m);
        this.teamMembersByKey.set(key, Array.from(byId.values()));
        if (this.carriedShape === shape) {
          this.carriedTeamMembers.set(teamId, Array.from(byId.values()));
        }
        const pagination = summary.members_pagination;
        const nextCursor = pagination?.has_next ? (pagination.next_cursor ?? null) : null;
        this.teamMemberPageByKey.set(key, { cursor: nextCursor, hasNext: nextCursor != null });
      });
    } catch (error) {
      console.error("Failed to load more group capacity members", { teamId, error });
    } finally {
      runInAction(() => {
        this.loadingTeams.delete(teamId);
      });
    }
  }

  get hasMoreGroups(): boolean {
    return Boolean(this.current?.groups_pagination?.has_next);
  }

  /** Fetch and append the next page of groups (teams / projects). New groups
   * arrive collapsed by default (absent from expandedGroups), matching the
   * initial load. */
  async loadMoreGroups(): Promise<void> {
    const slug = this.slug;
    if (!slug) return;
    const win = this.window;
    const key = this.summaryKey();
    const current = this.summaryByKey.get(key);
    const pagination = current?.groups_pagination;
    if (!current || !pagination?.has_next || this.isLoadingMore) return;
    runInAction(() => {
      this.isLoadingMore = true;
    });
    try {
      const next = await this.rootStore.workspaceCapacityService.getWorkspaceCapacity(
        slug,
        win.startIso,
        win.endIso,
        "day",
        {
          includeTotals: false,
          includeGroupMembers: false,
          includeMembers: false,
          includeMemberMetrics: false,
          rowGroup: this.rowGroup,
          groupPageSize: pagination.page_size,
          groupCursor: pagination.next_cursor,
          ...this.serverFilterOptions,
        },
      );
      runInAction(() => {
        const latest = this.summaryByKey.get(key);
        if (!latest) return;
        const merged = {
          ...latest,
          groups: [...(latest.groups ?? []), ...(next.groups ?? [])],
          groups_pagination: next.groups_pagination,
        };
        this.summaryByKey.set(key, merged);
        if (this.carriedSummary === latest) this.carriedSummary = merged;
      });
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  }

  /** True while a member's first/next task page is being fetched. */
  memberHasMoreTasks(memberId: string): boolean {
    return this.taskPageByKey.get(this.detailKey(memberId))?.hasNext ?? false;
  }

  /** Load the first page of an expanded member's tasks (server-paginated). */
  async fetchMemberDetails(memberId: string, force = false): Promise<void> {
    const slug = this.slug;
    if (!slug) return;
    const win = this.window;
    const key = this.detailKey(memberId);
    if ((!force && this.hasFreshRead(this.detailsByKey, key)) || this.loadingMembers.has(memberId))
      return;
    runInAction(() => {
      this.loadingMembers.add(memberId);
    });
    try {
      const summary = await this.rootStore.workspaceCapacityService.getWorkspaceCapacityDetails(
        slug,
        win.startIso,
        win.endIso,
        "day",
        {
          ...this.serverFilterOptions,
          memberIds: [memberId],
          taskOffset: 0,
          taskLimit: CAPACITY_TASK_PAGE_SIZE,
        },
      );
      const member = collectMembers(summary).find((m) => m.id === memberId);
      const shape = this.shapeKey();
      runInAction(() => {
        this.detailsByKey.set(key, member?.days ?? []);
        if (this.carriedShape === shape) this.carriedDetails.set(memberId, member?.days ?? []);
        this.taskPageByKey.set(key, {
          offset: 0,
          hasNext: summary.tasks_pagination?.has_next ?? false,
        });
      });
      this.markFresh(key);
    } catch (error) {
      console.error("Failed to load member capacity details", { memberId, error });
    } finally {
      runInAction(() => {
        this.loadingMembers.delete(memberId);
      });
    }
  }

  /** Fetch and append the next page of an expanded member's tasks. */
  async fetchMoreMemberTasks(memberId: string): Promise<void> {
    const slug = this.slug;
    if (!slug) return;
    const win = this.window;
    const key = this.detailKey(memberId);
    const page = this.taskPageByKey.get(key);
    if (!page?.hasNext || this.loadingMembers.has(memberId)) return;
    const nextOffset = page.offset + CAPACITY_TASK_PAGE_SIZE;
    runInAction(() => {
      this.loadingMembers.add(memberId);
    });
    try {
      const summary = await this.rootStore.workspaceCapacityService.getWorkspaceCapacityDetails(
        slug,
        win.startIso,
        win.endIso,
        "day",
        {
          ...this.serverFilterOptions,
          memberIds: [memberId],
          taskOffset: nextOffset,
          taskLimit: CAPACITY_TASK_PAGE_SIZE,
        },
      );
      const member = collectMembers(summary).find((m) => m.id === memberId);
      const shape = this.shapeKey();
      runInAction(() => {
        // Pages cover disjoint work items, so merging by date is append-only.
        const merged = mergeMemberDays(this.detailsByKey.get(key) ?? [], member?.days ?? []);
        this.detailsByKey.set(key, merged);
        if (this.carriedShape === shape) this.carriedDetails.set(memberId, merged);
        this.taskPageByKey.set(key, {
          offset: nextOffset,
          hasNext: summary.tasks_pagination?.has_next ?? false,
        });
      });
    } catch (error) {
      console.error("Failed to load more member tasks", { memberId, nextOffset, error });
    } finally {
      runInAction(() => {
        this.loadingMembers.delete(memberId);
      });
    }
  }

  async loadMorePeople(): Promise<void> {
    const slug = this.slug;
    if (!slug) return;
    const win = this.window;
    const key = this.summaryKey();
    const current = this.summaryByKey.get(key);
    const pagination = current?.members_pagination;
    if (!current || !pagination?.has_next || this.isLoadingMore) return;
    runInAction(() => {
      this.isLoadingMore = true;
    });
    try {
      const next = await this.rootStore.workspaceCapacityService.getWorkspaceCapacity(
        slug,
        win.startIso,
        win.endIso,
        this.zoom,
        {
          includeTotals: false,
          includeMemberMetrics: false,
          memberPageSize: pagination.page_size,
          memberCursor: pagination.next_cursor,
          rowGroup: this.rowGroup,
          ...this.serverFilterOptions,
        },
      );
      runInAction(() => {
        const latest = this.summaryByKey.get(key);
        if (!latest) return;
        const merged = {
          ...latest,
          members: [...(latest.members ?? []), ...(next.members ?? [])],
          members_pagination: next.members_pagination,
        };
        this.summaryByKey.set(key, merged);
        if (this.carriedSummary === latest) this.carriedSummary = merged;
      });
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  }
}

function collectMembers(summary: TWorkspaceCapacitySummary) {
  if (summary.members?.length) return summary.members;
  if (summary.groups?.length) return summary.groups.flatMap((g) => g.members ?? []);
  return [];
}

/** Append a later task page's day slices onto the already-loaded days. Pages
 * cover disjoint work items, so allocations concatenate without de-duping. */
function mergeMemberDays(
  existing: TWorkspaceCapacityMemberDay[],
  incoming: TWorkspaceCapacityMemberDay[],
): TWorkspaceCapacityMemberDay[] {
  if (existing.length === 0) return incoming;
  const byDate = new Map<string, TWorkspaceCapacityMemberDay>(
    existing.map((day) => [day.date, { ...day, allocations: [...(day.allocations ?? [])] }]),
  );
  for (const day of incoming) {
    const current = byDate.get(day.date);
    if (current) current.allocations = [...(current.allocations ?? []), ...(day.allocations ?? [])];
    else byDate.set(day.date, day);
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}
